import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bibliotecaDocumentosService } from '../../shared/services/bibliotecaDocumentosService';
import type { GrupoDocumento, CreateGrupoDocumentoDTO } from '../../shared/types';

export const AdminBibliotecasPage: React.FC = () => {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState<GrupoDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [grupoEditando, setGrupoEditando] = useState<GrupoDocumento | null>(null);

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bibliotecaDocumentosService.getAllGrupos(true); // Incluir inativos na tela de admin
      setGrupos(data.sort((a, b) => a.ordem - b.ordem));
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar grupos');
      console.error('Erro ao carregar grupos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async (grupo: CreateGrupoDocumentoDTO) => {
    try {
      if (grupoEditando) {
        await bibliotecaDocumentosService.updateGrupo(grupoEditando.id, grupo);
      } else {
        await bibliotecaDocumentosService.createGrupo(grupo);
      }
      await loadGrupos();
      setModalAberto(false);
      setGrupoEditando(null);
    } catch (err: any) {
      alert('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditar = (grupo: GrupoDocumento) => {
    setGrupoEditando(grupo);
    setModalAberto(true);
  };

  const handleDeletar = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este grupo?')) return;

    try {
      await bibliotecaDocumentosService.deleteGrupo(id);
      await loadGrupos();
    } catch (err: any) {
      alert('Erro ao deletar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleNovo = () => {
    setGrupoEditando(null);
    setModalAberto(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando grupos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadGrupos}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📚 Bibliotecas de Documentos</h1>
          <p className="text-gray-600 mt-2">Gerencie grupos de documentos e manuais técnicos</p>
        </div>
        <button
          onClick={handleNovo}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-lg flex items-center gap-2"
        >
          <span className="text-xl">➕</span>
          Novo Grupo
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-semibold">Total de Grupos</p>
              <p className="text-3xl font-bold text-blue-900">{grupos.length}</p>
            </div>
            <div className="text-4xl">📁</div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-semibold">Total de Documentos</p>
              <p className="text-3xl font-bold text-green-900">
                {grupos.reduce((acc, g) => acc + (g.totalDocumentos || 0), 0)}
              </p>
            </div>
            <div className="text-4xl">📄</div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-semibold">Empresas Vinculadas</p>
              <p className="text-3xl font-bold text-purple-900">
                {grupos.reduce((acc, g) => acc + (g.totalEmpresas || 0), 0)}
              </p>
            </div>
            <div className="text-4xl">🏢</div>
          </div>
        </div>
      </div>

      {/* Grid de Grupos */}
      {grupos.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhum grupo cadastrado</h3>
          <p className="text-gray-600 mb-6">Comece criando seu primeiro grupo de documentos</p>
          <button
            onClick={handleNovo}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            ➕ Criar Primeiro Grupo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {grupos.map((grupo) => (
            <div
              key={grupo.id}
              className="bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow overflow-hidden"
            >
              {/* Header do Card */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl">{grupo.icone || '📁'}</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      (grupo.ativo || (grupo as any).Ativo) ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  >
                    {(grupo.ativo || (grupo as any).Ativo) ? '✓ Ativo' : '✗ Inativo'}
                  </span>
                </div>
                <h3 className="text-xl font-bold truncate">{grupo.nome}</h3>
                <p className="text-blue-100 text-sm mt-2 line-clamp-2">
                  {grupo.descricao || 'Sem descrição'}
                </p>
              </div>

              {/* Conteúdo do Card */}
              <div className="p-6">
                {/* Stats */}
                <div className="flex justify-around mb-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{grupo.totalDocumentos || 0}</p>
                    <p className="text-xs text-gray-600">Documentos</p>
                  </div>
                  <div className="border-l border-gray-200"></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{grupo.totalEmpresas || 0}</p>
                    <p className="text-xs text-gray-600">Empresas</p>
                  </div>
                  <div className="border-l border-gray-200"></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">#{grupo.ordem}</p>
                    <p className="text-xs text-gray-600">Ordem</p>
                  </div>
                </div>

                {/* Ações */}
                <div className="space-y-2">
                  <button
                    onClick={() => (window.location.href = `/admin/bibliotecas/${grupo.id}`)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    📄 Ver Documentos
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => navigate(`/admin/bibliotecas/${grupo.id}/empresas`)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold text-sm"
                    >
                      🏢 Empresas
                    </button>
                    <button
                      onClick={() => handleEditar(grupo)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold text-sm"
                    >
                      ✏️ Editar
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeletar(grupo.id)}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-sm"
                  >
                    🗑️ Deletar
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                  Criado em: {new Date(grupo.dataCriacao).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar */}
      {modalAberto && (
        <ModalGrupo
          grupo={grupoEditando}
          onSalvar={handleSalvar}
          onCancelar={() => {
            setModalAberto(false);
            setGrupoEditando(null);
          }}
          proximaOrdem={grupos.length > 0 ? Math.max(...grupos.map((g) => g.ordem)) + 1 : 1}
        />
      )}
    </div>
  );
};

// ============================================================================
// MODAL DE GRUPO
// ============================================================================
interface ModalGrupoProps {
  grupo: GrupoDocumento | null;
  onSalvar: (grupo: CreateGrupoDocumentoDTO) => void;
  onCancelar: () => void;
  proximaOrdem: number;
}

const ModalGrupo: React.FC<ModalGrupoProps> = ({ grupo, onSalvar, onCancelar, proximaOrdem }) => {
  const [form, setForm] = useState<CreateGrupoDocumentoDTO>({
    nome: '',
    descricao: '',
    icone: '📁',
    ordem: proximaOrdem,
    ativo: true,
  });

  useEffect(() => {
    if (grupo) {
      setForm({
        nome: grupo.nome,
        descricao: grupo.descricao,
        icone: grupo.icone,
        ordem: grupo.ordem,
        ativo: grupo.ativo,
      });
    }
  }, [grupo]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    onSalvar(form);
  };

  const iconesComuns = ['📁', '📚', '📘', '📗', '📙', '🛡️', '⚙️', '📋', '🔧', '⚠️'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <h2 className="text-2xl font-bold">{grupo ? '✏️ Editar Grupo' : '➕ Novo Grupo'}</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nome */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nome do Grupo *
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Manuais Técnicos"
              required
              maxLength={100}
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Descrição</label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descrição do grupo de documentos..."
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Ícone e Ordem */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ícone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ícone (emoji)
              </label>
              <input
                type="text"
                value={form.icone}
                onChange={(e) => setForm({ ...form, icone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-3xl"
                maxLength={2}
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {iconesComuns.map((icone) => (
                  <button
                    key={icone}
                    type="button"
                    onClick={() => setForm({ ...form, icone })}
                    className="text-2xl p-2 hover:bg-gray-100 rounded border border-gray-200"
                  >
                    {icone}
                  </button>
                ))}
              </div>
            </div>

            {/* Ordem */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ordem de Exibição
              </label>
              <input
                type="number"
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min={0}
              />
              <p className="text-xs text-gray-600 mt-1">
                Grupos são ordenados em ordem crescente
              </p>
            </div>
          </div>

          {/* Ativo */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="ativo"
              checked={form.ativo}
              onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="ativo" className="ml-3 text-sm font-semibold text-gray-700">
              Grupo Ativo
            </label>
          </div>

          {/* Ações */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancelar}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              {grupo ? 'Salvar Alterações' : 'Criar Grupo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
